// Locale detection and formatting utilities
export const detectUserLocale = () => {
  return navigator.language || navigator.userLanguage || 'en-US';
};

export const detectUserLanguage = () => {
  const locale = detectUserLocale();
  return locale.split('-')[0] || 'en';
};

export const detectUserRegion = () => {
  const locale = detectUserLocale();
  const parts = locale.split('-');
  return parts[1] || parts[0].toUpperCase();
};

// Map regions to their default currencies
const REGION_TO_CURRENCY = {
  'US': 'USD', 'GB': 'GBP', 'AU': 'AUD', 'CA': 'CAD', 'NZ': 'NZD',
  'EU': 'EUR', 'DE': 'EUR', 'FR': 'EUR', 'ES': 'EUR', 'IT': 'EUR', 
  'NL': 'EUR', 'BE': 'EUR', 'AT': 'EUR', 'IE': 'EUR', 'PT': 'EUR',
  'FI': 'EUR', 'GR': 'EUR', 'LU': 'EUR', 'MT': 'EUR', 'CY': 'EUR',
  'SK': 'EUR', 'SI': 'EUR', 'EE': 'EUR', 'LV': 'EUR', 'LT': 'EUR',
  'JP': 'JPY', 'CN': 'CNY', 'TW': 'TWD', 'KR': 'KRW', 'HK': 'HKD',
  'TH': 'THB', 'VN': 'VND', 'PH': 'PHP', 'ID': 'IDR', 'MY': 'MYR', 'SG': 'SGD',
  'IN': 'INR', 'AE': 'AED', 'SA': 'SAR', 'QA': 'QAR', 'KW': 'KWD',
  'BR': 'BRL', 'MX': 'MXN', 'AR': 'ARS', 'CL': 'CLP', 'CO': 'COP', 'PE': 'PEN',
  'ZA': 'ZAR', 'EG': 'EGP', 'MA': 'MAD', 'NG': 'NGN', 'KE': 'KES',
  'CH': 'CHF', 'SE': 'SEK', 'NO': 'NOK', 'DK': 'DKK', 'PL': 'PLN', 
  'CZ': 'CZK', 'HU': 'HUF', 'RO': 'RON', 'BG': 'BGN', 'HR': 'HRK',
  'RU': 'RUB', 'UA': 'UAH', 'TR': 'TRY', 'IL': 'ILS', 'IS': 'ISK'
};

export const detectUserCurrency = () => {
  const region = detectUserRegion();
  return REGION_TO_CURRENCY[region] || 'USD';
};

// Format currency with proper locale
export const formatMoney = (amount, currency, options = {}) => {
  const locale = detectUserLocale();
  
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: options.minimumFractionDigits ?? 0,
      maximumFractionDigits: options.maximumFractionDigits ?? 2
    }).format(amount);
  } catch (e) {
    // Fallback for unsupported currencies
    return `${currency} ${amount.toFixed(2)}`;
  }
};

// Format number with proper locale (no currency symbol)
export const formatNumber = (amount, options = {}) => {
  const locale = detectUserLocale();
  
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: options.minimumFractionDigits ?? 0,
    maximumFractionDigits: options.maximumFractionDigits ?? 2
  }).format(amount);
};

// Format date with proper locale
export const formatDate = (date, options = {}) => {
  const locale = detectUserLocale();
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  return new Intl.DateTimeFormat(locale, {
    year: options.year ?? 'numeric',
    month: options.month ?? 'short',
    day: options.day ?? 'numeric',
    ...options
  }).format(dateObj);
};

// Format relative time (e.g., "2 days ago")
export const formatRelativeTime = (date) => {
  const locale = detectUserLocale();
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffInSeconds = Math.floor((now - dateObj) / 1000);
  
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  
  if (diffInSeconds < 60) return rtf.format(-diffInSeconds, 'second');
  if (diffInSeconds < 3600) return rtf.format(-Math.floor(diffInSeconds / 60), 'minute');
  if (diffInSeconds < 86400) return rtf.format(-Math.floor(diffInSeconds / 3600), 'hour');
  if (diffInSeconds < 2592000) return rtf.format(-Math.floor(diffInSeconds / 86400), 'day');
  if (diffInSeconds < 31536000) return rtf.format(-Math.floor(diffInSeconds / 2592000), 'month');
  return rtf.format(-Math.floor(diffInSeconds / 31536000), 'year');
};

// Get currency symbol
export const getCurrencySymbol = (currency) => {
  try {
    const formatted = new Intl.NumberFormat('en', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(0);
    return formatted.replace(/[0-9]/g, '').trim();
  } catch (e) {
    return currency;
  }
};
